import { useCallback } from "react";
import { atom, useRecoilCallback, useRecoilValue, useSetRecoilState } from "recoil";

import { getDocuments } from "api/documents/documents";

export interface DocumentUploadNavigationEntry {
  navId: string;
  billIds: string[];
}

export interface DocumentUploadNavigationFetchContext {
  query: Record<string, unknown>;
  page: number;
  size: number;
  pageOffset: number;
}

export interface DocumentUploadNavigationState {
  entries: DocumentUploadNavigationEntry[];
  totalCount: number;
  fetchContext?: DocumentUploadNavigationFetchContext | null;
}

const documentUploadNavigationState = atom<DocumentUploadNavigationState | null>({
  key: "documentUploadNavigationState",
  default: null,
});

export const useDocumentUploadNavigation = () =>
  useRecoilValue(documentUploadNavigationState);

export const useSetDocumentUploadNavigation = () => {
  const set = useSetRecoilState(documentUploadNavigationState);
  return useCallback(
    (state: DocumentUploadNavigationState | null) => set(state),
    [set],
  );
};

export const useFetchNextDocUploadPage = () =>
  useRecoilCallback(({ snapshot, set }) => async (): Promise<void> => {
    const state = await snapshot.getPromise(documentUploadNavigationState);
    const ctx = state?.fetchContext;
    if (!state || !ctx || state.entries.length >= state.totalCount) return;

    const nextPage = ctx.page + 1;
    const response = await getDocuments({ ...(ctx.query as any), page: nextPage, size: ctx.size });
    const newEntries: DocumentUploadNavigationEntry[] = (response.documents ?? []).map((doc: any) => ({
      navId: doc.bills?.length > 0 ? String(doc.bills[0].id) : String(doc.id),
      billIds: (doc.bills ?? []).map((b: any) => String(b.id)),
    }));

    set(documentUploadNavigationState, {
      entries: [...state.entries, ...newEntries],
      totalCount: response.count ?? state.totalCount,
      fetchContext: { ...ctx, page: nextPage },
    });
  }, []);

export const useFetchPrevDocUploadPage = () =>
  useRecoilCallback(({ snapshot, set }) => async (): Promise<void> => {
    const state = await snapshot.getPromise(documentUploadNavigationState);
    const ctx = state?.fetchContext;
    if (!state || !ctx || ctx.pageOffset === 0) return;

    const prevPageIndex = Math.floor(ctx.pageOffset / ctx.size) - 1;
    const response = await getDocuments({ ...(ctx.query as any), page: prevPageIndex, size: ctx.size });
    const newEntries: DocumentUploadNavigationEntry[] = (response.documents ?? []).map((doc: any) => ({
      navId: doc.bills?.length > 0 ? String(doc.bills[0].id) : String(doc.id),
      billIds: (doc.bills ?? []).map((b: any) => String(b.id)),
    }));

    set(documentUploadNavigationState, {
      entries: [...newEntries, ...state.entries],
      totalCount: response.count ?? state.totalCount,
      fetchContext: { ...ctx, pageOffset: ctx.pageOffset - newEntries.length },
    });
  }, []);
